import React, { useState } from "react"
import styled from "styled-components"
import { rem } from "polished"
import SanityImage from "gatsby-plugin-sanity-image"

import {
  SemiBdTitle5,
  SemiBdTitle3,
  SemiBdTitle4,
  RegBody,
  IconChevronDown,
} from "@sr/gatsby-theme-ui"

import { Link } from "gatsby"
import {
  type ProductsHubSolutionTab,
  type ProductsHubSolutionTabsProps,
  handleAnchorClick,
} from "./ProductsHubSolutionTabsMobile"

const ProductsHubSolutionTabsDesktop = ({
  tabs,
}: ProductsHubSolutionTabsProps) => {
  const [activeTab, setActiveTab] = useState<ProductsHubSolutionTab>(
    tabs?.productHubSolutionsTabItems[0]
  )

  return (
    <ProductsHubSolutionTabsDesktopStyled>
      <SemiBdTitle3>{tabs?.title}</SemiBdTitle3>
      <div className="products-hub-solutions-tabs-desktop__tabs-container">
        {tabs?.productHubSolutionsTabItems.map(
          (tab: ProductsHubSolutionTab, i: number) => {
            return (
              <div
                className={
                  "products-hub-solutions-tabs-desktop__tab-container" +
                  (activeTab.label === tab.label ? " active" : " null")
                }
                onClick={() => setActiveTab(tab)}
                key={i}
              >
                <SemiBdTitle5>{tab?.label}</SemiBdTitle5>
              </div>
            )
          }
        )}
      </div>
      <div className="products-hub-solutions-tabs-desktop__content-container">
        {activeTab?.img?.asset && (
          <SanityImage
            alt={activeTab?.img?.alt || "tab image"}
            asset={activeTab?.img?.asset}
            className="product-hub-solutions-tabs-desktop__tab-image"
            crop={activeTab?.img?.crop}
            hotspot={activeTab?.img?.hotspot}
          />
        )}
        <div className="products_hub-solutions-tabs-desktop__text-container">
          <SemiBdTitle5 className="products_hub-solutions-tabs-desktop__text-label">
            {activeTab?.label}
          </SemiBdTitle5>
          <SemiBdTitle4 className="products_hub-solutions-tabs-desktop__text-title">
            {activeTab?.title}
          </SemiBdTitle4>
          <RegBody className="products_hub-solutions-tabs-desktop__text-text">
            {activeTab?.text}
          </RegBody>
          <SemiBdTitle5 className="products_hub-solutions-tabs-desktop__text-solutions-title">
            {activeTab?.solutionsTitle}
          </SemiBdTitle5>
          {activeTab?.recommendedSolutions && (
            <div className="products-hub-solutions-tabs-desktop__recommended-container">
              {activeTab?.recommendedSolutions.map((solution, i) => {
                return (
                  <Link to={solution.recommendedSolution.path.current} key={i}>
                    <li>{solution.recommendedSolution.title}</li>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className="products-hub-solutions-tabs-desktop__link-container">
        <a
          onClick={() => handleAnchorClick()}
          className="products-hub-solutions-tabs-desktop__link"
        >
          <div className="products-hub-solutions-tabs-desktop__link-icon-container">
            <IconChevronDown color="var(--color-primary)" />
          </div>
          <SemiBdTitle4 className="products-hub-solutions-tabs-desktop__link-text">
            View all solutions
          </SemiBdTitle4>
        </a>
      </div>
    </ProductsHubSolutionTabsDesktopStyled>
  )
}

export default ProductsHubSolutionTabsDesktop

const ProductsHubSolutionTabsDesktopStyled = styled.section`
  max-width: ${rem(1030)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h3 {
    padding-top: ${rem(80)};
    max-width: ${rem(600)};
    text-align: center;
  }

  .products-hub-solutions-tabs-desktop__tabs-container {
    width: 100%;
    display: flex;
    padding-top: ${rem(64)};

    .products-hub-solutions-tabs-desktop__tab-container {
      width: ${rem(206)};
      display: flex;
      align-items: flex-end;
      justify-content: center;
      border-bottom: 2px solid var(--color-gray200);
      padding-bottom: ${rem(24)};
      cursor: pointer;

      h5 {
        max-width: ${rem(154)};
        text-align: center;
        color: var(--color-gray400);
      }
    }

    .active {
      border-bottom: 2px solid var(--color-gray900);

      h5 {
        color: var(--color-gray900);
      }
    }
  }

  .products-hub-solutions-tabs-desktop__content-container {
    padding-top: ${rem(56)};
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: ${rem(64)};

    .product-hub-solutions-tabs-desktop__tab-image {
      width: ${rem(402)};
      height: ${rem(408)};
      object-fit: cover;
      border-radius: ${rem(8)};
    }

    .products_hub-solutions-tabs-desktop__text-label {
      color: var(--color-primary);
    }

    .products_hub-solutions-tabs-desktop__text-title {
      padding-top: ${rem(8)};
    }

    .products_hub-solutions-tabs-desktop__text-text {
      padding-top: ${rem(16)};
    }

    .products_hub-solutions-tabs-desktop__text-solutions-title {
      padding-top: ${rem(24)};
    }

    .products-hub-solutions-tabs-desktop__recommended-container {
      padding: ${rem(16)} 0 0 ${rem(8)};
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      gap: ${rem(8)};
      max-height: ${rem(140)};

      li {
        font-weight: 600;
        font-size: ${rem(20)};
        line-height: ${rem(24)};
      }
    }
  }

  .products-hub-solutions-tabs-desktop__link-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: ${rem(56)};
    cursor: pointer;

    .products-hub-solutions-tabs-desktop__link {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: ${rem(8)};
      color: var(--color-primary);
      text-decoration: none;

      .products-hub-solutions-tabs-desktop__link-text {
        font-size: ${rem(20)};
        line-height: ${rem(32)};
      }
    }

    .products-hub-solutions-tabs-desktop__link-icon-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${rem(32)};
      height: ${rem(32)};
      border: ${rem(1)} solid var(--color-primary);
      border-radius: ${rem(32)};
    }
  }
`
